import React from 'react';
import styles from './MonthPicker.module.css';

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

interface MonthPickerProps {
  value?: number[];
  onChange?: (days: number[]) => void;
  disabled?: boolean;
}

/**
 * Multi-select grid for picking one or more days of the month (1–31).
 * 7-column CSS grid — all buttons are equal width, including the last row.
 * At least one day must remain selected.
 * Accepts `value` / `onChange` for Ant Design Form.Item compatibility.
 */
const MonthPicker: React.FC<MonthPickerProps> = ({
  value = [],
  onChange,
  disabled = false,
}) => {
  const handleToggle = (day: number) => {
    if (disabled) return;

    const newDays = value.includes(day)
      ? value.filter((d) => d !== day)
      : [...value, day].sort((a, b) => a - b);

    onChange?.(newDays);
  };

  return (
    <div
      className={styles.container}
      role='group'
      aria-label='Days of the month'
    >
      {DAYS.map((day) => {
        const selected = value.includes(day);
        return (
          <button
            key={day}
            type='button'
            role='checkbox'
            aria-checked={selected}
            aria-label={`Day ${day}`}
            className={[
              styles.day,
              selected ? styles.selected : '',
              disabled ? styles.disabled : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => handleToggle(day)}
            disabled={disabled}
          >
            {day}
          </button>
        );
      })}
    </div>
  );
};

export default MonthPicker;
